// Content preview renderer registry, keyed by surface. Each renderer lists the contexts it can
// show (tabs in the preview shell) and renders a PreviewModel for one context and device.
// Unknown surfaces fall back to the generic social post frame.
import type { ReactNode } from "react";
import type { PreviewDevice, PreviewModel } from "../../../../shared/content-preview";
import { BlogArticle, BlogSharedLink } from "./renderers/BlogRenderer";
import { SOCIAL_PRESETS, SocialPost } from "./renderers/SocialRenderer";
import { VideoFeedCard, VideoWatchPage } from "./renderers/VideoRenderer";

export interface PreviewContext {
  id: string;
  label: string;
}

export interface PreviewRenderer {
  id: string;
  label: string;
  /** Lower-case surfaces this renderer handles. */
  surfaces: readonly string[];
  /** The first context is the default. */
  contexts: readonly PreviewContext[];
  render(model: PreviewModel, context: string, device: PreviewDevice): ReactNode;
}

function socialRenderer(id: string, label: string, surfaces: string[], presets: Array<keyof typeof SOCIAL_PRESETS>): PreviewRenderer {
  return {
    id,
    label,
    surfaces,
    contexts: presets.map((preset) => ({ id: preset === presets[0] && presets.length === 1 ? "post" : preset, label: SOCIAL_PRESETS[preset]!.label })),
    render(model, context, device) {
      const preset = presets.length === 1 ? presets[0]! : (presets.find((candidate) => candidate === context) ?? presets[0]!);
      return <SocialPost model={model} preset={SOCIAL_PRESETS[preset]!} device={device} />;
    },
  };
}

export const GENERIC_RENDERER: PreviewRenderer = socialRenderer("generic", "Post", [], ["generic"]);

export const PREVIEW_RENDERERS: readonly PreviewRenderer[] = [
  {
    id: "blog",
    label: "Blog post",
    surfaces: ["blog", "website"],
    contexts: [{ id: "article", label: "Article" }, { id: "shared-link", label: "Shared link" }],
    render(model, context, device) {
      return context === "shared-link" ? <BlogSharedLink model={model} device={device} /> : <BlogArticle model={model} device={device} />;
    },
  },
  {
    id: "video",
    label: "Video",
    surfaces: ["youtube", "youtube_shorts", "video"],
    contexts: [{ id: "watch", label: "Watch page" }, { id: "feed", label: "Feed / search" }],
    render(model, context, device) {
      return context === "feed" ? <VideoFeedCard model={model} device={device} /> : <VideoWatchPage model={model} device={device} />;
    },
  },
  socialRenderer("linkedin", "LinkedIn-style post", ["linkedin", "linkedin_personal", "linkedin_company"], ["linkedin"]),
  socialRenderer("x", "X-style post", ["twitter", "x"], ["x"]),
  socialRenderer("bluesky", "Bluesky-style post", ["bluesky"], ["bluesky"]),
  socialRenderer("threads", "Threads-style post", ["threads"], ["threads"]),
  socialRenderer("instagram", "Instagram-style post", ["instagram"], ["instagram_square", "instagram_portrait"]),
  socialRenderer("pinterest", "Pin", ["pinterest"], ["pin"]),
];

/** The renderer for a surface; the generic post frame when none matches. */
export function resolveRenderer(surface: string | null | undefined): PreviewRenderer {
  const key = (surface ?? "").trim().toLowerCase();
  return PREVIEW_RENDERERS.find((renderer) => renderer.surfaces.includes(key)) ?? GENERIC_RENDERER;
}

/** A context the renderer supports, else its default. */
export function resolveContext(renderer: PreviewRenderer, context: string | null | undefined): string {
  return renderer.contexts.find((candidate) => candidate.id === context)?.id ?? renderer.contexts[0]!.id;
}
